import { useCallback, useEffect, useMemo, useRef } from 'react';
import { isNullish } from 'remeda';
import { useBrandsWithSupport } from 'src/graphql';
import { useConfirmationModal } from 'src/utils/hooks';

interface UseClientSegmentsParams {
  partnerId: number | null;
  selectedSegmentId: string | null;
  onSegmentChange: (segmentId: string | null) => void | Promise<void>;
  hasUnsavedChanges: boolean;
}

export const useClientSegments = ({
  partnerId,
  selectedSegmentId,
  onSegmentChange,
  hasUnsavedChanges,
}: UseClientSegmentsParams) => {
  const { confirm } = useConfirmationModal();
  const { brands: brandsFromQuery, loading: segmentsLoading } =
    useBrandsWithSupport(partnerId);
  const segments = useMemo(
    () =>
      [...(brandsFromQuery ?? [])].sort((a, b) => (b.name ?? '').localeCompare(a.name ?? '')),
    [brandsFromQuery],
  );
  const hasPartner = !isNullish(partnerId);
  const hasSegments = segments.length > 0;
  const initialSegmentSelectedForPartnerRef = useRef<number | null>(null);
  // Clearing the selector is the one legitimate way to sit with no segment.
  const segmentClearedByUserRef = useRef(false);

  const handleSegmentChange = useCallback(
    async (segmentId: string | null) => {
      const normalizedSegmentId = segmentId ? segmentId : null;

      if (normalizedSegmentId === selectedSegmentId) {
        return true;
      }

      if (hasUnsavedChanges) {
        const confirmed = await confirm({
          title: 'Discard unsaved changes?',
          body: "Switching segments discards the changes you haven't saved.",
          confirmLabel: 'Discard changes',
          cancelLabel: 'Keep editing',
          danger: true,
        });

        if (!confirmed) {
          return false;
        }
      }

      segmentClearedByUserRef.current = normalizedSegmentId === null;
      await onSegmentChange(normalizedSegmentId);
      return true;
    },
    [confirm, hasUnsavedChanges, onSegmentChange, selectedSegmentId],
  );

  useEffect(() => {
    initialSegmentSelectedForPartnerRef.current = null;
    segmentClearedByUserRef.current = false;
  }, [partnerId]);

  useEffect(() => {
    if (!hasPartner || segmentsLoading || !hasSegments || selectedSegmentId) {
      return;
    }

    if (initialSegmentSelectedForPartnerRef.current === partnerId) {
      return;
    }

    initialSegmentSelectedForPartnerRef.current = partnerId;
    const defaultSegmentId = String(segments[0].brandId);
    void handleSegmentChange(defaultSegmentId);
  }, [
    handleSegmentChange,
    hasSegments,
    hasPartner,
    partnerId,
    segments,
    segmentsLoading,
    selectedSegmentId,
  ]);

  // Waits for the segment to arrive, not for the dispatch. The auto-select ref is set
  // synchronously but the handler is async, so keying off it flashes content then skeleton.
  const isInitialSegmentSelectionPending =
    hasPartner &&
    !segmentsLoading &&
    hasSegments &&
    !selectedSegmentId &&
    !segmentClearedByUserRef.current;

  return {
    segments,
    segmentsLoading,
    hasPartner,
    hasSegments,
    handleSegmentChange,
    isInitialSegmentSelectionPending,
  };
};
